import React, { useEffect } from 'react';
import { FeatureFlags } from '@adp-wfn/mdf-core';

import { IMDFButtonProps, MDFButton } from './MDFButton';
import html2pdf from './html2pdf';

declare const window: any;

export interface IMDFPrintButtonProps extends IMDFButtonProps {
  // ID of element whose contents are to be printed.
  containerId: string;

  // Options specified here: https://github.com/eKoopmans/html2pdf.js#options
  pdfOptions?: object;

  // Whether to use the html2canvas library in the worker
  useHtmlToCanvasLibrary?: boolean;

  // Whether to use the browser's built-in print functionality
  useWindowPrint?: boolean;
}

export const MDFPrintButton = (props: IMDFPrintButtonProps) => {
  const { containerId, pdfOptions, useHtmlToCanvasLibrary, children, ...mdfButtonProps } = props;

  if (!containerId) {
    console.warn('MDFPrintButton required property "containerId" was not provided!');
  }

  useEffect(() => {
    if (!window.html2pdf) {
      window.WFNShell?.addScript('/theme/static/editor/html2pdf.bundle.min.js');
    }
  }, []);

  const printWithBrowser = (element: HTMLElement) => {
    const printClass = 'mdf-print-target-' + Date.now();
    element.classList.add(printClass);

    const hiddenEmployeeIdBars: { el: Element; prev: string | null }[] = [];

    document.querySelectorAll('wfn-employee-id-bar').forEach((el) => {
      if (!element.contains(el)) {
        hiddenEmployeeIdBars.push({ el, prev: el.getAttribute('style') });

        try {
          (el as HTMLElement).style.setProperty('display', 'none', 'important');
        }
        catch (e) {
          // ignore if cannot set style
        }
      }
    });

    const printStyle = document.createElement('style');
    printStyle.id = 'mdf-print-styles';
    printStyle.textContent = `
      @media print {
        body * {
          visibility: hidden !important;
        }
        body *:not([class*="ag-"]):not([id*="ag-"]):not([class*="mdf-print-target-"]) {
          position: static !important;
        }
        .${printClass}, .${printClass} * {
          visibility: visible !important;
          print-color-adjust: exact;
        }
        .${printClass} {
          position: absolute !important;
          left: 0 !important;
          top: 0 !important;
          width: 100% !important;
        }
      }
    `;

    document.head.appendChild(printStyle);

    const cleanup = () => {
      hiddenEmployeeIdBars.forEach(({ el, prev }) => {
        if (prev !== null) {
          el.setAttribute('style', prev);
        }
        else {
          el.removeAttribute('style');
        }
      });

      const styleElement = document.getElementById('mdf-print-styles');

      if (styleElement) {
        styleElement.remove();
      }

      element.classList.remove(printClass);
      window.removeEventListener('afterprint', cleanup);
    };

    window.addEventListener('afterprint', cleanup);
    window.print();
  };

  const onClick = () => {
    if (containerId) {
      const element = document.getElementById(containerId);

      if (element) {
        if (props.useWindowPrint) {
          printWithBrowser(element);
        }
        else if (FeatureFlags.hasFeature('wfnHtml2Pdf')) {
          void html2pdf(element, { ...pdfOptions, useHtmlToCanvasLibrary });
        }
        else if (window.html2pdf) {
          window.html2pdf(element, pdfOptions);
        }
      }
    }
  };

  return <MDFButton {...mdfButtonProps} onClick={onClick}>{children}</MDFButton>;
};
